import * as yup from 'yup';
import { ScreenType } from 'commons/models/referral';
import _values from 'lodash/values';

import {
	Row,
	RenderConfig,
	Column,
	TextInput,
	SelectInput,
	AssetUploadInputOnS3,
	EMPTY_ARRAY,
	ASSET_TYPE,
	NumberInput,
	BOOLEAN_OPTIONS,
	TextAreaInput
} from 'commons-ui';

import { CTA_TYPE_OPTIONS, CTA_VALUE_OPTIONS, HAPPY_HOUR_TYPES, HH_ZUPEE_GAMES } from './constants';
import { EHappyHourTypes, ScreenName } from '../interfaces';

export const FORM_FIELD_KEYS = {
	BG_COLOR: 'bgColor',
	CTA_BG_COLOR: 'ctaBgColor',
	CTA_DISPLAY_TEXT: 'ctaDisplayText',
	CTA_TEXT_COLOR: 'ctaTextColor',
	CTA_TYPE: 'ctaType',
	CTA_VALUE: 'ctaValue',
	DIVIDER_COLOR: 'dividerColor',
	END_HOUR: 'endHour',
	END_TIME: 'endTime',
	FG_URL: 'fgUrl',
	GAME_NAME: 'gameName',
	HH_TYPE: 'hhType',
	IMAGE_URL: 'imageUrl',
	IS_DISABLED: 'isDisabled',
	MARQUEE: 'marquee',
	MARQUEE_ANIM_URL: 'marqueeAnimUrl',
	MARQUEE_BG_COLOR: 'marqueeBgColor',
	MARQUEE_TEXT: 'marqueeText',
	MARQUEE_TEXT_COLOR: 'marqueeTextColor',
	MARQUEE_WIGGLE_COLOR: 'marqueeWiggleColor',
	PRIORITY: 'priority',
	SCREEN_NAME: 'screenName',
	START_HOUR: 'startHour',
	START_TIME: 'startTime',
	STRIPE_COLOR: 'stripeColor',
	STRIPE_URL: 'stripeUrl',
	SUBTITLE: 'subtitle',
	PRIMARY_TEXT: 'text1',
	PRIMARY_TEXT_COLOR: 'text1Color',
	SECONDARY_TEXT: 'text2',
	SECONDARY_TEXT_COLOR: 'text2Color',
	TIME_RANGE: 'timeText',
	TIME_RANGE_COLOR: 'timeTextColor',
	TITLE_URL: 'titleUrl'
};

const colorRequiredValidation = (fieldName: string) =>
	yup
		.string()
		.required(`${fieldName} co is required`)
		.matches(/^#[0-9a-f]{6}$/i, `${fieldName} should be a hexadecimal color code`);

export const validationSchema = yup.object().shape({
	bgColor: colorRequiredValidation('Background'),
	ctaBgColor: colorRequiredValidation('CTA BG'),
	ctaDisplayText: yup.string().required('CTA display text is required'),
	ctaTextColor: colorRequiredValidation('CTA Text'),
	ctaType: yup
		.string()
		.oneOf(_values(ScreenType), 'CTA Type should be one of IN_APP, DEEP_LINK, EXTERNAL_LINK, WEBVIEW_LINK')
		.required('CTA type is required'),
	ctaValue: yup.string().when('ctaType', {
		is: (value: string[]) => value != null,
		then: yup.string().oneOf(_values(ScreenName)).optional()
	}),
	dividerColor: colorRequiredValidation('Divider'),
	endHour: yup.number().min(0, 'End Hour must be greater than or equal to 0').required('End Hour is a required field'),
	endTime: yup.number().required('End Time is a required field'),
	fgUrl: yup.string().url().required('Base image is required'),
	gameName: yup.string().required('Game name is required'),
	hhType: yup.string().oneOf(_values(EHappyHourTypes), 'Happy hour type must be one of DEF, BONUS_REWARDS, BONUS_PERCENTAGE'),
	imageUrl: yup.string().url().required('Bonus shine circle image is required'),
	isDisabled: yup.boolean().required().default(true),
	marqueeAnimUrl: yup.string().url().required('Marquee ticker wave image is required'),
	marqueeBgColor: colorRequiredValidation('Marquee BG'),
	marqueeText: yup.string().required('Marquee string is required'),
	marqueeTextColor: colorRequiredValidation('Marquee text'),
	marqueeWiggleColor: yup.string().optional(),
	priority: yup.number().min(0, 'Priority should be greater than equal to 0').integer('Number should be an integer').required(),
	screenName: yup.string().oneOf(_values(ScreenName)).required(),
	startHour: yup.number().min(0, 'Start Hour must be greater than or equal to 0').required('Start Hour is a required field'),
	startTime: yup.number().required('Start Time is a required field'),
	stripeColor: yup
		.string()
		.optional()
		.matches(/^#[0-9a-f]{6}$/i, 'Color code should be heaxdecimal'),
	stripeUrl: yup.string().url().required('Stripe Image is required'),
	subtitle: yup.string().required('Subtitle is a required field'),
	text1: yup.string().required('Primary text is a required field'),
	text1Color: colorRequiredValidation('Primary text'),
	text2: yup.string().required('Secondary text is a required field'),
	text2Color: colorRequiredValidation('Secondary text'),
	timeText: yup.string().required('Time text is a required field'),
	timeTextColor: colorRequiredValidation('Time text'),
	titleUrl: yup.string().url().required('Happy Hour text image is required'),
	_id: yup.string().optional()
});

export const FORM_FIELD_CONFIG = {
	[FORM_FIELD_KEYS.BG_COLOR]: new RenderConfig()
		.setName('bgColor')
		.setLabel('Background Color')
		.setRenderer(TextInput)
		.setRenderProps({ isEditable: { create: true, edit: true }, required: true }),
	[FORM_FIELD_KEYS.CTA_BG_COLOR]: new RenderConfig()
		.setName('ctaBgColor')
		.setLabel('CTA BG Color')
		.setRenderer(TextInput)
		.setRenderProps({ isEditable: { create: true, edit: true }, required: true }),
	[FORM_FIELD_KEYS.CTA_DISPLAY_TEXT]: new RenderConfig()
		.setName('ctaDisplayText')
		.setLabel('CTA Text')
		.setRenderer(TextInput)
		.setRenderProps({ isEditable: { create: true, edit: true }, required: true }),
	[FORM_FIELD_KEYS.CTA_TEXT_COLOR]: new RenderConfig()
		.setName('ctaTextColor')
		.setLabel('CTA Text Color')
		.setRenderer(TextInput)
		.setRenderProps({ isEditable: { create: true, edit: true }, required: true }),
	[FORM_FIELD_KEYS.CTA_TYPE]: new RenderConfig()
		.setName('ctaType')
		.setLabel('CTA Type')
		.setRenderer(SelectInput)
		.setRenderProps({ isEditable: { create: true, edit: true }, options: CTA_TYPE_OPTIONS, required: true }),
	[FORM_FIELD_KEYS.CTA_VALUE]: new RenderConfig()
		.setName('ctaValue')
		.setLabel('CTA Value')
		.setRenderer(SelectInput)
		.setRenderProps({ isEditable: { create: true, edit: true }, options: CTA_VALUE_OPTIONS, required: false }),
	[FORM_FIELD_KEYS.DIVIDER_COLOR]: new RenderConfig()
		.setName('dividerColor')
		.setLabel('Divider Color')
		.setRenderer(TextInput)
		.setRenderProps({ required: true }),
	[FORM_FIELD_KEYS.END_HOUR]: new RenderConfig()
		.setName('endHour')
		.setLabel('End Hour')
		.setRenderer(NumberInput)
		.setRenderProps({ required: true }),
	[FORM_FIELD_KEYS.END_TIME]: new RenderConfig()
		.setName('endTime')
		.setLabel('End Time')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			inputType: 'datetime-local',
			format: 'yyyy-dd-mm HH:MM:ss',
			required: true
		}),
	[FORM_FIELD_KEYS.FG_URL]: new RenderConfig()
		.setName('fgUrl')
		.setLabel('Foreground Base Image')
		.setRenderer(AssetUploadInputOnS3)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: true,
			assetType: ASSET_TYPE.IMAGE,
			maxFileSizeMB: 0.1
		}),
	[FORM_FIELD_KEYS.GAME_NAME]: new RenderConfig()
		.setName('gameName')
		.setLabel('Game Name')
		.setRenderer(SelectInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			options: HH_ZUPEE_GAMES,
			required: true
		}),
	[FORM_FIELD_KEYS.HH_TYPE]: new RenderConfig()
		.setName('hhType')
		.setLabel('Happy Hour Type')
		.setRenderer(SelectInput)
		.setRenderProps({
			isEditable: { create: true, edit: false },
			options: HAPPY_HOUR_TYPES,
			required: true
		}),
	[FORM_FIELD_KEYS.IMAGE_URL]: new RenderConfig()
		.setName('imageUrl')
		.setLabel('Bonus Shine URL')
		.setRenderer(AssetUploadInputOnS3)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: true,
			assetType: ASSET_TYPE.MISC_ASSET,
			maxFileSizeMB: 0.4,
			usePresignedUrl: true
		}),
	[FORM_FIELD_KEYS.IS_DISABLED]: new RenderConfig()
		.setName('isDisabled')
		.setLabel('Disabled')
		.setRenderer(SelectInput)
		.setRenderProps({ isEditable: { create: true, edit: true }, options: BOOLEAN_OPTIONS, required: true }),
	[FORM_FIELD_KEYS.MARQUEE_ANIM_URL]: new RenderConfig()
		.setName('marqueeAnimUrl')
		.setLabel('Marquee Ticker Wave URL')
		.setRenderer(AssetUploadInputOnS3)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: true,
			assetType: ASSET_TYPE.MISC_ASSET,
			maxFileSizeMB: 0.4,
			usePresignedUrl: true
		}),
	[FORM_FIELD_KEYS.MARQUEE_BG_COLOR]: new RenderConfig()
		.setName('marqueeBgColor')
		.setLabel('Marquee BG color')
		.setRenderer(TextInput)
		.setRenderProps({ isEditable: { create: true, edit: true }, required: true }),
	[FORM_FIELD_KEYS.MARQUEE_TEXT]: new RenderConfig()
		.setName('marqueeText')
		.setLabel('Marquee Text')
		.setRenderer(TextInput)
		.setRenderProps({ isEditable: { create: true, edit: true }, required: true }),
	[FORM_FIELD_KEYS.MARQUEE_TEXT_COLOR]: new RenderConfig()
		.setName('marqueeTextColor')
		.setLabel('Marquee Text Color')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: true
		}),
	[FORM_FIELD_KEYS.MARQUEE_WIGGLE_COLOR]: new RenderConfig()
		.setName('marqueeWiggleColor')
		.setLabel('Marquee Wiggle Color')
		.setRenderer(TextInput)
		.setRenderProps({ isEditable: { create: true, edit: true }, required: false }),
	[FORM_FIELD_KEYS.PRIORITY]: new RenderConfig()
		.setName('priority')
		.setLabel('Priority')
		.setRenderer(NumberInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: true
		}),
	[FORM_FIELD_KEYS.SCREEN_NAME]: new RenderConfig()
		.setName('screenName')
		.setLabel('Screen Name')
		.setRenderer(SelectInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			options: CTA_VALUE_OPTIONS,
			required: true
		}),
	[FORM_FIELD_KEYS.START_HOUR]: new RenderConfig()
		.setName('startHour')
		.setLabel('Start Hour')
		.setRenderer(NumberInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: true
		}),
	[FORM_FIELD_KEYS.START_TIME]: new RenderConfig()
		.setName('startTime')
		.setLabel('Start Time')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			inputType: 'datetime-local',
			format: 'yyyy-dd-mm HH:MM:ss',
			required: true
		}),
	[FORM_FIELD_KEYS.STRIPE_COLOR]: new RenderConfig()
		.setName('stripeColor')
		.setLabel('Stripe Color')
		.setRenderer(TextInput)
		.setRenderProps({ isEditable: { create: true, edit: true }, required: false }),
	[FORM_FIELD_KEYS.STRIPE_URL]: new RenderConfig()
		.setName('stripeUrl')
		.setLabel('Stripe Time Range URL')
		.setRenderer(AssetUploadInputOnS3)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: true,
			assetType: ASSET_TYPE.MISC_ASSET,
			maxFileSizeMB: 0.4,
			usePresignedUrl: true
		}),
	[FORM_FIELD_KEYS.SUBTITLE]: new RenderConfig()
		.setName('subtitle')
		.setLabel('Subtitle')
		.setRenderer(TextInput)
		.setRenderProps({ required: true }),
	[FORM_FIELD_KEYS.PRIMARY_TEXT]: new RenderConfig()
		.setName('text1')
		.setLabel('Primary Text')
		.setRenderer(TextAreaInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: true
		}),
	[FORM_FIELD_KEYS.PRIMARY_TEXT_COLOR]: new RenderConfig()
		.setName('text1Color')
		.setLabel('Primary Text Color')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: true
		}),
	[FORM_FIELD_KEYS.SECONDARY_TEXT]: new RenderConfig()
		.setName('text2')
		.setLabel('Secondary Text')
		.setRenderer(TextAreaInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: true
		}),
	[FORM_FIELD_KEYS.SECONDARY_TEXT_COLOR]: new RenderConfig()
		.setName('text2Color')
		.setLabel('Secondary Text Color')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: true
		}),

	[FORM_FIELD_KEYS.TIME_RANGE]: new RenderConfig()
		.setName('timeText')
		.setLabel('Time Range')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: true
		}),

	[FORM_FIELD_KEYS.TIME_RANGE_COLOR]: new RenderConfig()
		.setName('timeTextColor')
		.setLabel('Time Range Color')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: true
		}),
	[FORM_FIELD_KEYS.TITLE_URL]: new RenderConfig()
		.setName('titleUrl')
		.setLabel('Happy Hour Text Image')
		.setRenderer(AssetUploadInputOnS3)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: true,
			assetType: ASSET_TYPE.IMAGE,
			maxFileSizeMB: 0.1
		})
};

export const FORM_LAYOUT_CONFIG = [
	new Row(null, EMPTY_ARRAY, [
		new Column([new Row(FORM_FIELD_KEYS.BG_COLOR)]),
		new Column([new Row(FORM_FIELD_KEYS.CTA_BG_COLOR)])
	]),
	new Row(null, EMPTY_ARRAY, [
		new Column([new Row(FORM_FIELD_KEYS.CTA_DISPLAY_TEXT)]),
		new Column([new Row(FORM_FIELD_KEYS.CTA_TEXT_COLOR)])
	]),
	new Row(null, EMPTY_ARRAY, [new Column([new Row(FORM_FIELD_KEYS.CTA_TYPE)]), new Column([new Row(FORM_FIELD_KEYS.CTA_VALUE)])]),
	new Row(null, EMPTY_ARRAY, [new Column([new Row(FORM_FIELD_KEYS.DIVIDER_COLOR)])]),
	new Row(null, EMPTY_ARRAY, [new Column([new Row(FORM_FIELD_KEYS.START_TIME)]), new Column([new Row(FORM_FIELD_KEYS.END_TIME)])]),
	new Row(null, EMPTY_ARRAY, [new Column([new Row(FORM_FIELD_KEYS.START_HOUR)]), new Column([new Row(FORM_FIELD_KEYS.END_HOUR)])]),
	new Row(null, EMPTY_ARRAY, [
		new Column([new Row(FORM_FIELD_KEYS.PRIMARY_TEXT)]),
		new Column([new Row(FORM_FIELD_KEYS.PRIMARY_TEXT_COLOR)])
	]),
	new Row(null, EMPTY_ARRAY, [
		new Column([new Row(FORM_FIELD_KEYS.SECONDARY_TEXT)]),
		new Column([new Row(FORM_FIELD_KEYS.SECONDARY_TEXT_COLOR)])
	]),
	new Row(null, EMPTY_ARRAY, [
		new Column([
			new Row(null, EMPTY_ARRAY, [
				new Column([new Row(FORM_FIELD_KEYS.MARQUEE_TEXT)]),
				new Column([new Row(FORM_FIELD_KEYS.MARQUEE_TEXT_COLOR)])
			])
		])
	]),
	new Row(FORM_FIELD_KEYS.MARQUEE, EMPTY_ARRAY, [
		new Column([
			new Row(null, EMPTY_ARRAY, [
				new Column([new Row(FORM_FIELD_KEYS.MARQUEE_WIGGLE_COLOR)]),
				new Column([new Row(FORM_FIELD_KEYS.MARQUEE_BG_COLOR)])
			])
		])
	]),
	new Row(FORM_FIELD_KEYS.MARQUEE_ANIM_URL),
	new Row(null, EMPTY_ARRAY, [
		new Column([
			new Row(null, EMPTY_ARRAY, [
				new Column([new Row(FORM_FIELD_KEYS.GAME_NAME)]),
				new Column([new Row(FORM_FIELD_KEYS.HH_TYPE)])
			])
		])
	]),
	new Row(FORM_FIELD_KEYS.IMAGE_URL),
	new Row(FORM_FIELD_KEYS.FG_URL),
	new Row(null, EMPTY_ARRAY, [
		new Column([
			new Row(null, EMPTY_ARRAY, [
				new Column([new Row(FORM_FIELD_KEYS.IS_DISABLED)]),
				new Column([new Row(FORM_FIELD_KEYS.SUBTITLE)])
			])
		])
	]),
	new Row(FORM_FIELD_KEYS.TITLE_URL),
	new Row(null, EMPTY_ARRAY, [
		new Column([new Row(FORM_FIELD_KEYS.TIME_RANGE)]),
		new Column([new Row(FORM_FIELD_KEYS.TIME_RANGE_COLOR)])
	]),
	new Row(null, EMPTY_ARRAY, [new Column([new Row(FORM_FIELD_KEYS.PRIORITY)]), new Column([new Row(FORM_FIELD_KEYS.SCREEN_NAME)])]),

	new Row(null, EMPTY_ARRAY, [
		new Column([new Row(FORM_FIELD_KEYS.STRIPE_COLOR)]),
		new Column([new Row(FORM_FIELD_KEYS.STRIPE_URL)])
	])
];
